// ── Cross-agent intent detection (ASC-10513) ─────────────────────────────────
//
// The client half of the shared "detect → run self → queue others" pattern: an
// agent asks the orchestrator which agents a user's query spans, so it can offer a
// hand-off while answering its own slice locally. Pairs with the headless
// `useCrossAgentHandoff` hook in `@platform/agent-sdk-react`, which turns a
// detection into a sequential offer queue over `route-handoff.ts`.
//
// The detection RESULT contract lives here (pure, framework-agnostic) rather than
// in the React package, so it is the single source both the hook and this
// orchestrator-backed detector share.

import { z } from 'zod'
import { safeFetch } from './safe-fetch'
import type { RouteCandidate } from './agent-routing'

/** What a detector returns — the orchestrator's classify verdict + route candidates. */
export type CrossAgentDetection = {
  /** The orchestrator's execution mode; only `'lookup'` produces offers. `null` when
   *  detection could not run (degraded), which yields no offers. */
  mode: string | null
  candidates: readonly RouteCandidate[]
}

/** Runs cross-agent intent detection for one query. Injected into the hook so it is
 *  backend-agnostic and unit-testable; `createOrchestratorDetector` is the real one. */
export type CrossAgentDetector = (query: string) => Promise<CrossAgentDetection>

// The orchestrator's `DetectResponse` (services/orchestrator .../routes/detect.py).
// `reason` is carried but unused by the routing logic. `accessible` is the per-agent
// layer-2 flag (ASC-11159): false = the caller has no role on this agent, so the client
// shows the hand-off but denies it (contact-admin card). Optional so an older backend
// (which omits it) reads every candidate as reachable.
const DetectResponseSchema = z.object({
  mode: z.string(),
  candidates: z.array(
    z.object({
      domain_id: z.string(),
      score: z.number(),
      reason: z.string().optional(),
      accessible: z.boolean().optional(),
    }),
  ),
})

/**
 * A detector that calls the orchestrator's stateless `/detect` endpoint through the
 * platform BFF proxy (same-origin, HTTP-only cookie auth).
 *
 * Best-effort by contract: ANY failure — a non-2xx, a validation miss, a network error,
 * or a 403 for a user without orchestrator access — resolves to "no routing" rather than
 * throwing, so a detection problem never disrupts the host agent's own answer.
 */
export function createOrchestratorDetector(workspaceId: string): CrossAgentDetector {
  return async (query: string): Promise<CrossAgentDetection> => {
    const result = await safeFetch(
      `/api/orchestrator/v1/workspaces/${encodeURIComponent(workspaceId)}/detect`,
      DetectResponseSchema,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request: query }),
      },
    )
    if (!result.ok) return { mode: null, candidates: [] }
    return { mode: result.data.mode, candidates: result.data.candidates }
  }
}
